import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users, Lock, Clock, Check } from "lucide-react";
import { COMMUNITIES } from "@/constants/testIds";

/**
 * CommunityCard — public teaser for a community. Adapts to visibility +
 * membership state:
 *   • public + not joined     → Join
 *   • public + joined         → Joined chip
 *   • private + not joined    → 🔒 lock icon + "Request to Join"
 *   • private + pending req   → "Requested" chip (disabled)
 *   • private + joined        → Joined chip → tap to open
 *
 * Non-members of private communities never see the description or cover
 * image (server strips them). Tapping the card opens the detail page
 * where the full request flow lives.
 */
export default function CommunityCard({ community, onPrimaryAction }) {
  const isPrivate = community.visibility === "private";
  const joined = !!community.joined;
  const pending = !!community.has_pending_request;
  const showCover = !isPrivate || joined;

  let cta = { label: "Join", variant: "primary", disabled: false, testid: COMMUNITIES.joinButton(community.id) };
  if (joined) cta = { label: "Open", variant: "muted", disabled: false, testid: `community-open-${community.id}` };
  else if (isPrivate && pending) cta = { label: "Requested", variant: "chip", disabled: true, testid: `community-requested-${community.id}` };
  else if (isPrivate || community.join_policy === "request") cta = { label: "Request to Join", variant: "primary", disabled: false, testid: `community-request-${community.id}` };

  const onClickPrimary = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPrimaryAction?.(community);
  };

  return (
    <Link
      to={`/c/${community.id}`}
      data-testid={COMMUNITIES.card(community.id)}
      className="card-surface overflow-hidden hover-lift flex flex-col block"
    >
      <div className="aspect-[16/9] relative overflow-hidden">
        {showCover && community.cover_url ? (
          <img
            src={community.cover_url}
            alt={community.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-rose-900/30 to-black flex items-center justify-center">
            {isPrivate && !joined && (
              <Lock size={32} className="text-rose-500/40" />
            )}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
        {isPrivate && (
          <span
            data-testid={`community-private-badge-${community.id}`}
            className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-black/70 backdrop-blur-md border border-rose-500/30 text-rose-300 rounded-full px-2 py-1"
          >
            <Lock size={10} /> Private
          </span>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h3 className="font-display text-xl mb-1">{community.name}</h3>
        {isPrivate && !joined ? (
          <p className="text-xs text-zinc-500 leading-relaxed mb-4 italic">
            Members-only community — request to unlock.
          </p>
        ) : (
          <p className="text-sm text-zinc-400 leading-relaxed mb-4 line-clamp-2">
            {community.description || "No description yet."}
          </p>
        )}
        <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Users size={14} />
            {(community.members_count || 0).toLocaleString()} members
          </div>
          <Button
            data-testid={cta.testid}
            onClick={onClickPrimary}
            disabled={cta.disabled}
            className={`rounded-none px-4 py-2 text-xs tracking-wide inline-flex items-center gap-1 ${
              cta.variant === "chip"
                ? "bg-amber-400/10 border border-amber-400/40 text-amber-300 cursor-default"
                : cta.variant === "muted"
                ? "bg-transparent border border-white/15 text-white hover:border-rose-600"
                : "btn-crimson"
            }`}
          >
            {cta.label === "Requested" && <Clock size={12} />}
            {cta.label === "Open" && <Check size={12} />}
            {cta.label}
          </Button>
        </div>
      </div>
    </Link>
  );
}
